import React from 'react';
import { Spin, Tooltip } from 'antd';
import { RocketOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useProjectQA } from '@hooks/projects/useProjectQA';
import QAKanbanBoard from '@/components/module/projects/qa_tab/QAKanbanBoard';
import QaSubmitToPmoModal from '@/components/module/projects/qa_tab/QaSubmitToPmoModal';
import TaskDetailModal from '@/components/module/projects/task/TaskDetailModal';
import QASubmissionDetailModal from '@/components/module/projects/qa_tab/QASubmissionDetailModal';
import { SliderTab } from '@/components/ui/SliderTab';
import AppModal from '../AppModal';
import SprintDetailSummary from '@/components/module/projects/sprints_tab/SprintDetailSummary';
import SprintKanbanBoardShimmer from '@/components/module/projects/sprints_tab/SprintKanbanBoardShimmer';
import {
    formatDate,
    milestoneHasSprintsWithTasks,
    getMilestoneDisplayLabel,
    getSprintDisplayLabel,
    getPhaseDisplayTitle,
} from '@hooks/projects/projectHelpers';
import {
    EmptyStateBlock,
    DetailPanelLoading,
    BoardKanbanBoardShell,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabCountBadge,
    MutedText,
} from '../style';

const QA_MILESTONE_DISABLED_HINT =
    'No tasks in this milestone yet. Add tasks to a sprint in this milestone before QA can open.';

export default function ProjectQATab({ projectId, project }) {
    const {
        milestones,
        selectedMilestoneId,
        setSelectedMilestoneId,
        sprintsForMilestone,
        selectedSprintId,
        setSelectedSprintId,
        selectedSprint,
        todos,
        todoSubmissionGroups,
        pmoSubmissionGroups,
        bugsReported,
        submittedToPmo,
        isLoading,
        isBoardFetching,
        selectedTask,
        selectedSubmission,
        taskDescriptionMarkdown,
        refreshSelectedTaskDetail,
        closePanel,
        showBugForm,
        openBugForm,
        closeBugForm,
        editingPendingBugId,
        pendingBugs,
        removePendingBug,
        startEditPendingBug,
        bugForm,
        saveBugDraft,
        submitAllBugs,
        createBugPending,
        bugPendingAttachments,
        bugPendingLinks,
        handleBugFilesSelected,
        removeBugAttachment,
        bugLinkModalOpen,
        bugLinkForm,
        handleBugLinkRequest,
        closeBugLinkModal,
        handleBugLinkModalSubmit,
        removeBugPendingLink,
        handleBugEditorLinkRemoved,
        qaTaskDetailBugs,
        handleTaskSelect,
        openSubmissionDetail,
        closeSubmissionDetail,
        handlePmoSubmittedTaskOpenDetail,
        dropTarget,
        draggingColumn,
        draggingSelectedCount,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        pmoSubmissionOpen,
        closePmoSubmissionPanel,
        pmoSubmissionItems,
        pmoSubmissionDescriptionHtml,
        setPmoSubmissionDescriptionHtml,
        pmoSubmissionPendingFiles,
        pmoSubmissionPendingLinks,
        handlePmoFilesSelected,
        removePmoPendingFile,
        removePmoPendingLink,
        handlePmoLinkRequest,
        handlePmoEditorLinkRemoved,
        pmoLinkModalOpen,
        pmoLinkForm,
        closePmoLinkModal,
        handlePmoLinkModalSubmit,
        pmoSubmissionSession,
        removeFromPmoSubmission,
        submitPmoFromModal,
        submitToPMOPending,
        qaTodoPmoSelectMode,
        qaTodoPmoSelectedIds,
        toggleQaTodoPmoTaskSelected,
        startQaTodoPmoSelectMode,
        cancelQaTodoPmoSelectMode,
        selectAllQaTodoTasks,
        unselectAllQaTodoTasks,
        resolvedTasks,
        qaResolvedPmoSelectMode,
        qaResolvedPmoSelectedIds,
        toggleQaResolvedPmoTaskSelected,
        startQaResolvedPmoSelectMode,
        cancelQaResolvedPmoSelectMode,
        selectAllQaResolvedTasks,
        unselectAllQaResolvedTasks,
        openPmoSubmissionAllResolved,
        openPmoSubmissionFromResolvedSelection,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
        openPmoSubmissionAllTodos,
        openPmoSubmissionFromTodoSelection,
        useSprintsEnabled,
        noSprintsMode,
        useMilestonesEnabled,
        usePhasesEnabled,
        isTodoPmoTaskDisabled,
        isResolvedPmoTaskDisabled,
        qaSubmissions,
    } = useProjectQA(projectId, project);

    const [sprintDetailModalOpen, setSprintDetailModalOpen] = React.useState(false);

    const mainContent = (
        <BoardTabRoot
            style={{
                flex: 1,
                minWidth: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',

            }}
        >
            <BoardTabRoot
                style={{
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    paddingRight: 24,
                    backgroundColor: 'transparent',
                    '--board-kanban-inline-gutter': 'clamp(16px, 4vw, 40px)',
                }}
            >
                {(useMilestonesEnabled || usePhasesEnabled) && Array.isArray(milestones) && milestones.length > 0 && (
                    <Container $marginBottom="24px" $flexShrink="0">
                        <SliderTab
                            items={milestones.map((m) => ({
                                key: m.id,
                                label: useMilestonesEnabled
                                    ? (getMilestoneDisplayLabel(m) || m.name || 'Milestone')
                                    : (getPhaseDisplayTitle(m) || m.title || 'Phase'),
                            }))}
                            activeKey={selectedMilestoneId}
                            onChange={(key) => {
                                const item = milestones.find((m) => String(m.id) === String(key));
                                if (item) setSelectedMilestoneId(item.id);
                            }}
                            fitContent
                        />
                    </Container>
                )}

                <Container
                    $display="flex"
                    $justifyContent="space-between"
                    $alignItems="flex-start"
                    $gap="16px"
                    $flexWrap="wrap"
                    style={{ marginBottom: 16 }}
                >
                    <div>
                        <BoardPageTitle>Quality Assurance</BoardPageTitle>
                        <BoardPageSubtitle>
                            {useSprintsEnabled
                                ? 'Create sprints of projects and manage tasks here.'
                                : 'Manage project tasks and submissions here.'}
                        </BoardPageSubtitle>
                    </div>
                    <Container $display="flex" $alignItems="center" $gap="8px" $flexWrap="wrap" />
                </Container>

                {!noSprintsMode && sprintsForMilestone.length > 0 ? (
                    <BoardUnderlineTabRow $sticky style={{ marginBottom: 24, marginTop: 10 , backgroundColor:'transparent'}}>
                        <BoardUnderlineTabInner>
                            {sprintsForMilestone.map((s) => {
                                const active = String(selectedSprintId) === String(s.id);
                                const label = getSprintDisplayLabel(s);
                                return (
                                    <BoardUnderlineTab
                                        key={s.id}
                                        type="button"
                                        $active={active}
                                        onClick={() => setSelectedSprintId(s.id)}
                                        title={label}
                                    >
                                        <BoardUnderlineTabLine>
                                            <BoardUnderlineTabPrimaryLabel $active={active}>
                                                {label}
                                            </BoardUnderlineTabPrimaryLabel>
                                            {active ? (
                                                <Tooltip
                                                    title="Sprint Detail"
                                                    placement="top"
                                                    color="rgba(17,24,39,0.96)"
                                                    mouseEnterDelay={0.05}
                                                    styles={{
                                                        body: {
                                                            fontSize: 11,
                                                            fontWeight: 600,
                                                            lineHeight: 1.1,
                                                            borderRadius: 7,
                                                            padding: '6px 9px',
                                                        }
                                                    }}
                                                >
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        className="sprint-tab-info-trigger"
                                                        aria-label="Sprint Detail"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSprintDetailModalOpen(true);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setSprintDetailModalOpen(true);
                                                            }
                                                        }}
                                                    >
                                                        <InfoCircleOutlined style={{ fontSize: 14 }} />
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                        </BoardUnderlineTabLine>
                                    </BoardUnderlineTab>
                                );
                            })}
                        </BoardUnderlineTabInner>
                    </BoardUnderlineTabRow>
                ) : !noSprintsMode ? (
                    <MutedText style={{ fontSize: 14, marginBottom: 24 }}>
                        {milestones?.length
                            ? (useMilestonesEnabled
                                ? 'No sprints for this milestone yet.'
                                : 'No sprints for this phase yet.')
                            : 'No sprints yet. Create a sprint to plan work.'}
                    </MutedText>
                ) : null}

                {isBoardFetching ? (
                    <BoardKanbanBoardShell>
                        <SprintKanbanBoardShimmer />
                    </BoardKanbanBoardShell>
                ) : !selectedSprint ? (
                    <>
                        {sprintsForMilestone.length > 0 && (
                            <MutedText style={{ fontSize: 14, marginBottom: 16 }}>
                                No tasks in any sprint for this view yet. Add tasks to a sprint to use the QA board.
                            </MutedText>
                        )}
                        <BoardKanbanBoardShell>
                            <Container
                                $display="flex"
                                $flexDirection="column"
                                $flex="1"
                                $minHeight="0"
                                $overflow="hidden"
                                style={{ width: '100%' }}
                            >
                                <QAKanbanBoard
                                    sprint={null}
                                    formatDateFn={formatDate}
                                    todoTasks={[]}
                                    todoSubmissionGroups={[]}
                                    bugsTasks={[]}
                                    resolvedTasks={[]}
                                    pmoTasks={[]}
                                    pmoSubmissionGroups={[]}
                                    onTaskSelect={handleTaskSelect}
                                    dropTarget={dropTarget}
                                    draggingColumn={draggingColumn}
                                    draggingSelectedCount={draggingSelectedCount}
                                    onDndDragStart={handleDndDragStart}
                                    onDndDragOverColumn={handleDndDragOverColumn}
                                    onDndDragEnd={handleDndDragEnd}
                                    todoPmoSelectMode={false}
                                    todoPmoSelectedIds={[]}
                                    onTodoPmoToggleTask={toggleQaTodoPmoTaskSelected}
                                    onTodoPmoStartSelectMode={startQaTodoPmoSelectMode}
                                    onTodoPmoCancelSelectMode={cancelQaTodoPmoSelectMode}
                                    onTodoPmoSelectAll={selectAllQaTodoTasks}
                                    onTodoPmoUnselectAll={unselectAllQaTodoTasks}
                                    onTodoPmoSubmitAll={openPmoSubmissionAllTodos}
                                    onTodoPmoSubmitSelection={openPmoSubmissionFromTodoSelection}
                                    resolvedPmoSelectMode={false}
                                    resolvedPmoSelectedIds={[]}
                                    onResolvedPmoToggleTask={toggleQaResolvedPmoTaskSelected}
                                    onResolvedPmoStartSelectMode={startQaResolvedPmoSelectMode}
                                    onResolvedPmoCancelSelectMode={cancelQaResolvedPmoSelectMode}
                                    onResolvedPmoSelectAll={selectAllQaResolvedTasks}
                                    onResolvedPmoUnselectAll={unselectAllQaResolvedTasks}
                                    onResolvedPmoSubmitAll={openPmoSubmissionAllResolved}
                                    onResolvedPmoSubmitSelection={openPmoSubmissionFromResolvedSelection}
                                    onPmoSubmittedTaskOpenDetail={handlePmoSubmittedTaskOpenDetail}
                                />
                            </Container>
                        </BoardKanbanBoardShell>
                    </>
                ) : (
                    <BoardKanbanBoardShell>
                        <Container
                            $display="flex"
                            $flexDirection="column"
                            $flex="1"
                            $minHeight="0"
                            $overflow="hidden"
                            style={{ width: '100%' }}
                        >
                            <QAKanbanBoard
                                sprint={selectedSprint}
                                formatDateFn={formatDate}
                                todoTasks={todos}
                                todoSubmissionGroups={todoSubmissionGroups}
                                bugsTasks={bugsReported}
                                resolvedTasks={resolvedTasks}
                                pmoTasks={submittedToPmo}
                                pmoSubmissionGroups={pmoSubmissionGroups}
                                onTaskSelect={handleTaskSelect}
                                onSubmissionOpen={openSubmissionDetail}
                                dropTarget={dropTarget}
                                draggingColumn={draggingColumn}
                                draggingSelectedCount={draggingSelectedCount}
                                onDndDragStart={handleDndDragStart}
                                onDndDragOverColumn={handleDndDragOverColumn}
                                onDndDragEnd={handleDndDragEnd}
                                todoPmoSelectMode={qaTodoPmoSelectMode}
                                todoPmoSelectedIds={qaTodoPmoSelectedIds}
                                onTodoPmoToggleTask={toggleQaTodoPmoTaskSelected}
                                onTodoPmoStartSelectMode={startQaTodoPmoSelectMode}
                                onTodoPmoCancelSelectMode={cancelQaTodoPmoSelectMode}
                                onTodoPmoSelectAll={selectAllQaTodoTasks}
                                onTodoPmoUnselectAll={unselectAllQaTodoTasks}
                                onTodoPmoSubmitAll={openPmoSubmissionAllTodos}
                                // We use openPmoSubmissionFromTodoSelection for selection submission
                                onTodoPmoSubmitSelection={openPmoSubmissionFromTodoSelection}
                                resolvedPmoSelectMode={qaResolvedPmoSelectMode}
                                resolvedPmoSelectedIds={qaResolvedPmoSelectedIds}
                                onResolvedPmoToggleTask={toggleQaResolvedPmoTaskSelected}
                                onResolvedPmoStartSelectMode={startQaResolvedPmoSelectMode}
                                onResolvedPmoCancelSelectMode={cancelQaResolvedPmoSelectMode}
                                onResolvedPmoSelectAll={selectAllQaResolvedTasks}
                                onResolvedPmoUnselectAll={unselectAllQaResolvedTasks}
                                onResolvedPmoSubmitAll={openPmoSubmissionAllResolved}
                                onResolvedPmoSubmitSelection={openPmoSubmissionFromResolvedSelection}
                                onPmoSubmittedTaskOpenDetail={handlePmoSubmittedTaskOpenDetail}
                                isTodoPmoTaskDisabled={isTodoPmoTaskDisabled}
                                isResolvedPmoTaskDisabled={isResolvedPmoTaskDisabled}
                                qaSubmissions={qaSubmissions}
                            />
                        </Container>
                    </BoardKanbanBoardShell>
                )}
            </BoardTabRoot>
        </BoardTabRoot>
    );

    return (
        <>
            {isLoading ? (
                <Container
                    style={{
                        margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <EmptyStateBlock>
                        <DetailPanelLoading>
                            <Spin size="large" />
                        </DetailPanelLoading>
                    </EmptyStateBlock>
                </Container>
            ) : (
                <Container $display="flex" $flex="1" $minHeight="0" $overflow="hidden">
                    {mainContent}
                </Container>
            )}

            <QaSubmitToPmoModal
                open={pmoSubmissionOpen}
                onCancel={closePmoSubmissionPanel}
                pmoSubmissionItems={pmoSubmissionItems}
                pmoSubmissionDescriptionHtml={pmoSubmissionDescriptionHtml}
                setPmoSubmissionDescriptionHtml={setPmoSubmissionDescriptionHtml}
                pmoSubmissionPendingFiles={pmoSubmissionPendingFiles}
                pmoSubmissionPendingLinks={pmoSubmissionPendingLinks}
                handlePmoFilesSelected={handlePmoFilesSelected}
                removePmoPendingFile={removePmoPendingFile}
                removePmoPendingLink={removePmoPendingLink}
                handlePmoLinkRequest={handlePmoLinkRequest}
                handlePmoEditorLinkRemoved={handlePmoEditorLinkRemoved}
                pmoLinkModalOpen={pmoLinkModalOpen}
                pmoLinkForm={pmoLinkForm}
                closePmoLinkModal={closePmoLinkModal}
                handlePmoLinkModalSubmit={handlePmoLinkModalSubmit}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
                pmoSubmissionSession={pmoSubmissionSession}
                removeFromPmoSubmission={removeFromPmoSubmission}
                submitPmoFromModal={submitPmoFromModal}
                submitToPMOPending={submitToPMOPending}
                onOpenTaskDetail={handleTaskSelect}
            />
            <TaskDetailModal
                projectId={projectId}
                open={Boolean(selectedTask?.task)}
                onClose={closePanel}
                onRequestRefresh={refreshSelectedTaskDetail}
                task={selectedTask?.task ?? null}
                taskDescriptionMarkdown={taskDescriptionMarkdown}
                taskBugs={qaTaskDetailBugs}
                hideBugsSection={Boolean(selectedTask?.hideBugsSection)}
                readOnlyBugActions={Boolean(selectedTask?.readOnlyBugActions)}
                showBugForm={showBugForm}
                openBugForm={openBugForm}
                closeBugForm={closeBugForm}
                editingPendingBugId={editingPendingBugId}
                pendingBugs={pendingBugs}
                removePendingBug={removePendingBug}
                startEditPendingBug={startEditPendingBug}
                bugForm={bugForm}
                saveBugDraft={saveBugDraft}
                submitAllBugs={submitAllBugs}
                createBugPending={createBugPending}
                bugPendingAttachments={bugPendingAttachments}
                bugPendingLinks={bugPendingLinks}
                handleBugFilesSelected={handleBugFilesSelected}
                removeBugAttachment={removeBugAttachment}
                bugLinkModalOpen={bugLinkModalOpen}
                bugLinkForm={bugLinkForm}
                handleBugLinkRequest={handleBugLinkRequest}
                closeBugLinkModal={closeBugLinkModal}
                handleBugLinkModalSubmit={handleBugLinkModalSubmit}
                removeBugPendingLink={removeBugPendingLink}
                handleBugEditorLinkRemoved={handleBugEditorLinkRemoved}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
            />

            <QASubmissionDetailModal
                open={Boolean(selectedSubmission?.group)}
                onClose={closeSubmissionDetail}
                sidLabel={selectedSubmission?.group?.sidLabel}
                submissionTitle={selectedSubmission?.group?.submissionTitle}
                submissionStatus={selectedSubmission?.group?.statusLabel}
                descriptionMarkdown={selectedSubmission?.group?.descriptionMarkdown}
                tasks={selectedSubmission?.group?.tasks ?? []}
                rawSubmission={selectedSubmission?.group?.rawSubmission ?? null}
                defaultTab={selectedSubmission?.defaultTab ?? 'tasks'}
                onTaskClick={(task) => {
                    if (!selectedSubmission?.sprint || !task) return;
                    handleTaskSelect(selectedSubmission.sprint, task, {
                        readOnlyBugActions: false,
                        defaultBugsTab: true,
                    });
                }}
            />

            <AppModal
                title="Sprint Detail"
                open={sprintDetailModalOpen}
                onCancel={() => setSprintDetailModalOpen(false)}
                footer={null}
                size="md"
            >
                <Container
                    $display="flex"
                    $flexDirection="column"
                    $gap="8px"
                    $padding="10px 6px 6px"
                >
                    {selectedSprint && (
                        <SprintDetailSummary
                            sprint={selectedSprint}
                            formatDateFn={formatDate}
                        />
                    )}
                </Container>
            </AppModal>
        </>
    );
}
